import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth";
import Shell from "@/components/shell/Shell";
import Login from "@/pages/Login";
import Tickets from "@/pages/Tickets";
import Board from "@/pages/Board";
import Channels from "@/pages/Channels";
import Users from "@/pages/Users";
import Permissions from "@/pages/Permissions";
import Rules from "@/pages/Rules";
import Reports from "@/pages/Reports";
import Backups from "@/pages/Backups";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-full grid place-items-center text-muted-foreground mono text-xs" data-testid="auth-loading">AUTHENTICATING…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function Gate({ perm, children }) {
  const { can } = useAuth();
  if (perm && !can(perm)) return <Navigate to="/tickets" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" storageKey="tt-theme">
      <TooltipProvider delayDuration={200}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Protected><Shell /></Protected>}>
                <Route index element={<Navigate to="/tickets" replace />} />
                <Route path="tickets" element={<Tickets />} />
                <Route path="tickets/:id" element={<Tickets />} />
                <Route path="board" element={<Gate perm="board.view"><Board /></Gate>} />
                <Route path="channels" element={<Gate perm="channels.manage"><Channels /></Gate>} />
                <Route path="users" element={<Gate perm="users.manage"><Users /></Gate>} />
                <Route path="permissions" element={<Gate perm="permissions.manage"><Permissions /></Gate>} />
                <Route path="automation" element={<Gate perm="rules.manage"><Rules /></Gate>} />
                <Route path="reports" element={<Gate perm="reports.view"><Reports /></Gate>} />
                <Route path="backups" element={<Gate perm="backups.manage"><Backups /></Gate>} />
                <Route path="audit" element={<Gate perm="audit.view"><Audit /></Gate>} />
                <Route path="settings" element={<Gate perm="settings.manage"><Settings /></Gate>} />
              </Route>
              <Route path="*" element={<Navigate to="/tickets" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="bottom-right" richColors closeButton />
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}
